import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BRAND } from "@/lib/site";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  subject: "software-development",
  message: "",
};

export const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your message is on its way.");
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="surface-canvas py-12 sm:py-16 md:py-24"
    >
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6">
        {/* Centered Editorial Header */}
        <div className="flex flex-col items-center text-center">
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-heading max-w-[800px] text-[#222222]"
          >
            Start a project. Or begin your training.
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-4 sm:mt-6 text-body-editorial max-w-[640px] text-[#222222]"
          >
            Whether you have a product brief to build or are looking to join our
            next cohort, send us a note and our team will get back to you within 24 hours.
          </motion.p>
        </div>

        <div className="mt-10 sm:mt-14 max-w-[960px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
          {/* Contact Details Column */}
          <div className="md:col-span-5 space-y-4 sm:space-y-6">
            <div className="bg-[#f7f7f7] rounded-[12px] p-5 sm:p-6 border border-[#ebebeb]">
              <p className="text-[12px] sm:text-[13px] font-medium text-[#6a6a6a] uppercase tracking-wider">
                Direct Inquiries
              </p>
              <a
                href={`mailto:${BRAND.contact.email}`}
                data-testid="contact-email"
                className="mt-2 block text-[16px] sm:text-[17px] font-medium text-[#222222] hover:text-[#6a6a6a] transition-colors break-all"
              >
                {BRAND.contact.email}
              </a>
              <a
                href={`tel:${BRAND.contact.phone.replace(/\s/g, "")}`}
                data-testid="contact-phone"
                className="mt-1 block text-[14px] sm:text-[15px] text-[#6a6a6a] hover:text-[#222222] transition-colors"
              >
                {BRAND.contact.phone}
              </a>
            </div>

            <div className="bg-[#f7f7f7] rounded-[12px] p-5 sm:p-6 border border-[#ebebeb]">
              <p className="text-[12px] sm:text-[13px] font-medium text-[#6a6a6a] uppercase tracking-wider">
                Studio Location
              </p>
              <p
                data-testid="contact-address"
                className="mt-2 text-[14px] sm:text-[15px] text-[#222222] leading-relaxed"
              >
                {BRAND.contact.address}
              </p>
            </div>
          </div>

          {/* Form Column */}
          <div className="md:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="space-y-3.5 sm:space-y-4"
              noValidate
            >
              <div>
                <label className="block text-[12px] sm:text-[13px] font-medium text-[#6a6a6a] mb-1" htmlFor="name">
                  Full Name
                </label>
                <input
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  className="editorial-input-box"
                  placeholder="Jane Doe"
                  data-testid="contact-input-name"
                  autoComplete="name"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 sm:gap-4">
                <div>
                  <label className="block text-[12px] sm:text-[13px] font-medium text-[#6a6a6a] mb-1" htmlFor="email">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    className="editorial-input-box"
                    placeholder="you@company.com"
                    data-testid="contact-input-email"
                    autoComplete="email"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[12px] sm:text-[13px] font-medium text-[#6a6a6a] mb-1" htmlFor="phone">
                    Phone Number (optional)
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    className="editorial-input-box"
                    placeholder="+91 ..."
                    data-testid="contact-input-phone"
                    autoComplete="tel"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[12px] sm:text-[13px] font-medium text-[#6a6a6a] mb-1" htmlFor="subject">
                  Topic of Interest
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  data-testid="contact-input-subject"
                  className="editorial-input-box appearance-none bg-white cursor-pointer"
                >
                  <option value="software-development">Software Development</option>
                  <option value="industry-training">Industry Training</option>
                  <option value="internship">6-month Internship</option>
                  <option value="hiring">Hiring from Innovate IT</option>
                  <option value="other">General Inquiry</option>
                </select>
              </div>

              <div>
                <label className="block text-[12px] sm:text-[13px] font-medium text-[#6a6a6a] mb-1" htmlFor="message">
                  Message / Project Brief
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={onChange}
                  className="editorial-input-box resize-none"
                  placeholder="Share a brief overview of your goals..."
                  data-testid="contact-input-message"
                  required
                />
              </div>

              <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                <p className="text-[12px] text-[#6a6a6a]">
                  We respect your privacy. No spam, ever.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="btn-primary-action w-full sm:w-auto disabled:opacity-50"
                >
                  {loading ? "Sending…" : "Submit message"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
